/**
 * 열교환기 튜브 파열 시 소요 분출용량 산정.
 *
 * 근거: API Standard 521, 2020년 6월판 4.4.14.2.2
 *
 * ⚠ **표준은 이 케이스에 닫힌 계산식을 주지 않는다.** 대신 설계 근거와
 *   어떤 유동식을 쓸지만 규정한다. 이 모듈은 그 지시를 그대로 구현한 것이다.
 *
 *   정상상태(steady-state) 설계 근거 — 4.4.14.2.2 a)~c):
 *     a) 튜브 1개가 예리하게 절단(sharp break)된다.
 *     b) 파단은 튜브시트 뒷면에서 일어난다고 본다.
 *     c) 고압측 유체는 튜브시트에 남은 튜브 스텁과 반대편 긴 튜브 양쪽으로
 *        흐른다 → **개구부가 2개**다.
 *
 *   "긴 개방 튜브"로 보는 대신 **오리피스 2개**로 단순화해도 된다.
 *   표준은 이쪽이 더 큰(보수적인) 유량을 준다고 명시한다.
 *
 *   유동식 선택 — 4.4.14.2.2:
 *     · 통과 중 플래싱하지 않는 액체 → 비압축성 유동식
 *     · 기체 → 압축성 유동식
 *     · 플래싱 액체·이상유체 → 이상유동(HEM 등) 방법. 이 모듈 범위 밖이며
 *       API 520 Annex C(이상유동 사이징)를 함께 검토해야 한다.
 *
 * 단위: 압력 kPa(절대) · 온도 K · 밀도 kg/m³ · 지름 mm · 유량 kg/h
 */

/** 기체 상수 (J/kmol·K) */
const R_UNIVERSAL = 8314.462618;

/**
 * 예리하게 절단된 튜브 단부의 배출계수.
 * 표준이 값을 규정하지 않으므로 실무 관례값을 기본으로 둔다.
 * 날카로운 모서리 오리피스 0.61, 보수적으로 크게 볼 때 0.7~0.75 를 쓴다.
 */
export const DEFAULT_TUBE_DISCHARGE_COEFFICIENT = 0.61;

/** 개구부 개수 — 4.4.14.2.2 c) 에 따라 스텁 + 긴 튜브 = 2 */
export const TUBE_BREAK_OPENINGS = 2;

/** 파단 개구부의 총 유효 면적 (m²). 튜브 내경 기준, 기본 2개소. */
export function breakAreaM2(
  tubeInnerDiameterMm: number,
  openings: number = TUBE_BREAK_OPENINGS,
): number {
  const dM = tubeInnerDiameterMm / 1000;
  return openings * (Math.PI / 4) * dM * dM;
}

export interface TubeRuptureFailure {
  readonly ok: false;
  readonly reason: "invalid-input" | "use-two-phase-method";
  readonly message: string;
}

function invalid(message: string): TubeRuptureFailure {
  return { ok: false, reason: "invalid-input", message };
}

/* ── 비플래싱 액체 (비압축성 유동식) ─────────────────────── */

export interface TubeRuptureLiquidInput {
  /** 튜브 내경 (mm) */
  readonly tubeInnerDiameterMm: number;
  /** 고압측 압력 (kPa 절대) */
  readonly highSidePressureKpaAbs: number;
  /** 저압측 배출 압력 (kPa 절대) — PSV 배출 조건의 쉘측 압력 */
  readonly lowSidePressureKpaAbs: number;
  /** 고압측 액체 밀도 (kg/m³) */
  readonly densityKgM3: number;
  /** 배출계수. 기본 0.61 */
  readonly dischargeCoefficient?: number;
  /** 개구부 개수. 기본 2 */
  readonly openings?: number;
}

export interface TubeRuptureLiquidSuccess {
  readonly ok: true;
  readonly kind: "liquid";
  /** 소요 분출용량 (kg/h) */
  readonly massFlowKgH: number;
  /** 체적유량 (m³/h) */
  readonly volumeFlowM3H: number;
  /** 파단 개구부 총 면적 (mm²) */
  readonly breakAreaMm2: number;
  /** ΔP (kPa) */
  readonly pressureDifferenceKpa: number;
  readonly warnings: readonly string[];
}

export type TubeRuptureLiquidResult =
  | TubeRuptureLiquidSuccess
  | TubeRuptureFailure;

/**
 * 플래싱하지 않는 액체의 튜브 파열 유량 — 비압축성 오리피스식.
 *
 *   W = Cd × A × √(2 ρ ΔP)      [kg/s], ΔP 는 Pa
 */
export function tubeRuptureLiquidRate(
  input: TubeRuptureLiquidInput,
): TubeRuptureLiquidResult {
  const {
    tubeInnerDiameterMm: d,
    highSidePressureKpaAbs: p1,
    lowSidePressureKpaAbs: p2,
    densityKgM3: rho,
  } = input;

  if (!Number.isFinite(d) || d <= 0) return invalid("튜브 내경은 0보다 커야 합니다.");
  if (!Number.isFinite(rho) || rho <= 0) return invalid("밀도는 0보다 커야 합니다.");
  if (!Number.isFinite(p1) || !Number.isFinite(p2)) {
    return invalid("압력이 올바르지 않습니다.");
  }
  if (p1 <= p2) {
    return invalid(
      "고압측 압력이 저압측 배출 압력보다 커야 합니다. 두 값이 절대압인지 확인하십시오.",
    );
  }

  const Cd = input.dischargeCoefficient ?? DEFAULT_TUBE_DISCHARGE_COEFFICIENT;
  const openings = input.openings ?? TUBE_BREAK_OPENINGS;
  if (!Number.isFinite(Cd) || Cd <= 0 || Cd > 1) {
    return invalid("배출계수는 0보다 크고 1 이하이어야 합니다.");
  }
  if (!Number.isInteger(openings) || openings < 1) {
    return invalid("개구부 개수는 1 이상의 정수이어야 합니다.");
  }

  const A = breakAreaM2(d, openings);
  const dpPa = (p1 - p2) * 1000;
  const massFlowKgS = Cd * A * Math.sqrt(2 * rho * dpPa);

  const warnings: string[] = [
    "파단 개구부를 오리피스 2개로 본 단순화입니다. 표준은 이 가정이 " +
      "'긴 개방 튜브'로 보는 것보다 큰(보수적인) 유량을 준다고 밝힙니다(4.4.14.2.2).",
    `배출계수 ${Cd} 를 적용했습니다. 표준은 값을 규정하지 않으므로 ` +
      "사내 기준에 맞춰 조정하고 근거를 남기십시오.",
    "저압측 배관으로 빠져나가는 용량은 별도로 공제할 수 있습니다(4.4.14.2.4).",
  ];
  if (openings !== TUBE_BREAK_OPENINGS) {
    warnings.push(
      `개구부를 ${openings} 개로 지정했습니다. 표준의 기본 가정은 튜브 스텁과 ` +
        "긴 튜브 양쪽, 즉 2개소입니다(4.4.14.2.2 c).",
    );
  }

  return {
    ok: true,
    kind: "liquid",
    massFlowKgH: massFlowKgS * 3600,
    volumeFlowM3H: (massFlowKgS / rho) * 3600,
    breakAreaMm2: A * 1e6,
    pressureDifferenceKpa: p1 - p2,
    warnings,
  };
}

/* ── 기체 (압축성 유동식) ────────────────────────────────── */

export interface TubeRuptureGasInput {
  /** 튜브 내경 (mm) */
  readonly tubeInnerDiameterMm: number;
  /** 고압측 압력 (kPa 절대) */
  readonly highSidePressureKpaAbs: number;
  /** 저압측 배출 압력 (kPa 절대) */
  readonly lowSidePressureKpaAbs: number;
  /** 고압측 온도 (K) */
  readonly temperatureK: number;
  /** 분자량 (kg/kmol) */
  readonly molecularWeight: number;
  /** 비열비 k */
  readonly specificHeatRatio: number;
  /** 압축계수 Z. 기본 1.0 */
  readonly compressibility?: number;
  /** 배출계수. 기본 0.61 */
  readonly dischargeCoefficient?: number;
  /** 개구부 개수. 기본 2 */
  readonly openings?: number;
}

export interface TubeRuptureGasSuccess {
  readonly ok: true;
  readonly kind: "gas";
  /** 소요 분출용량 (kg/h) */
  readonly massFlowKgH: number;
  /** 임계 유동 여부 */
  readonly critical: boolean;
  /** 임계 압력비 (2/(k+1))^(k/(k−1)) */
  readonly criticalPressureRatio: number;
  /** 실제 압력비 P2/P1 */
  readonly pressureRatio: number;
  readonly breakAreaMm2: number;
  readonly warnings: readonly string[];
}

export type TubeRuptureGasResult = TubeRuptureGasSuccess | TubeRuptureFailure;

/** 임계(초크) 압력비 — (2/(k+1))^(k/(k−1)) */
export function criticalPressureRatio(k: number): number {
  return Math.pow(2 / (k + 1), k / (k - 1));
}

/**
 * 기체의 튜브 파열 유량 — 압축성 오리피스식.
 *
 *   임계:  W = Cd·A·P1·√( k·M/(Z·R·T) · (2/(k+1))^((k+1)/(k−1)) )
 *   아임계: W = Cd·A·P1·√( 2·M/(Z·R·T) · k/(k−1) · [r^(2/k) − r^((k+1)/k)] )
 *
 * P1 은 Pa, A 는 m², W 는 kg/s.
 */
export function tubeRuptureGasRate(
  input: TubeRuptureGasInput,
): TubeRuptureGasResult {
  const {
    tubeInnerDiameterMm: d,
    highSidePressureKpaAbs: p1,
    lowSidePressureKpaAbs: p2,
    temperatureK: T,
    molecularWeight: M,
    specificHeatRatio: k,
  } = input;

  if (!Number.isFinite(d) || d <= 0) return invalid("튜브 내경은 0보다 커야 합니다.");
  if (!Number.isFinite(T) || T <= 0) return invalid("온도는 0 K 보다 커야 합니다.");
  if (!Number.isFinite(M) || M <= 0) return invalid("분자량은 0보다 커야 합니다.");
  if (!Number.isFinite(k) || k <= 1) return invalid("비열비 k 는 1보다 커야 합니다.");
  if (!Number.isFinite(p1) || !Number.isFinite(p2) || p2 < 0) {
    return invalid("압력이 올바르지 않습니다.");
  }
  if (p1 <= p2) {
    return invalid(
      "고압측 압력이 저압측 배출 압력보다 커야 합니다. 두 값이 절대압인지 확인하십시오.",
    );
  }

  const Z = input.compressibility ?? 1.0;
  const Cd = input.dischargeCoefficient ?? DEFAULT_TUBE_DISCHARGE_COEFFICIENT;
  const openings = input.openings ?? TUBE_BREAK_OPENINGS;
  if (!Number.isFinite(Z) || Z <= 0) return invalid("압축계수 Z 는 0보다 커야 합니다.");
  if (!Number.isFinite(Cd) || Cd <= 0 || Cd > 1) {
    return invalid("배출계수는 0보다 크고 1 이하이어야 합니다.");
  }
  if (!Number.isInteger(openings) || openings < 1) {
    return invalid("개구부 개수는 1 이상의 정수이어야 합니다.");
  }

  const A = breakAreaM2(d, openings);
  const p1Pa = p1 * 1000;
  const r = p2 / p1;
  const rc = criticalPressureRatio(k);
  const critical = r <= rc;

  const base = M / (Z * R_UNIVERSAL * T);
  const term = critical
    ? k * base * Math.pow(2 / (k + 1), (k + 1) / (k - 1))
    : 2 * base * (k / (k - 1)) * (Math.pow(r, 2 / k) - Math.pow(r, (k + 1) / k));

  const massFlowKgS = Cd * A * p1Pa * Math.sqrt(Math.max(term, 0));

  const warnings: string[] = [
    "파단 개구부를 오리피스 2개로 본 단순화입니다(4.4.14.2.2 c, 보수적 가정).",
    `배출계수 ${Cd}, 압축계수 ${Z} 를 적용했습니다. 표준이 값을 규정하지 않으므로 ` +
      "근거를 남기십시오.",
    critical
      ? "임계(초크) 유동입니다 — 저압측 압력이 변해도 유량은 늘지 않습니다."
      : "아임계 유동입니다 — 저압측 압력에 유량이 민감하므로 배출 조건을 확인하십시오.",
    "저압측 배관 용량 공제는 4.4.14.2.4 를 따르십시오.",
  ];

  return {
    ok: true,
    kind: "gas",
    massFlowKgH: massFlowKgS * 3600,
    critical,
    criticalPressureRatio: rc,
    pressureRatio: r,
    breakAreaMm2: A * 1e6,
    warnings,
  };
}

/**
 * 플래싱 액체·이상유체는 이 모듈에서 다루지 않는다.
 * 표준은 HEM 등 이상유동 방법을 쓰라고 규정한다(4.4.14.2.2).
 */
export function tubeRuptureTwoPhaseGuidance(): TubeRuptureFailure {
  return {
    ok: false,
    reason: "use-two-phase-method",
    message:
      "고압측 유체가 파단부를 지나며 플래싱하거나 이상유체이면 단상 유동식을 " +
      "쓸 수 없습니다. API 521 4.4.14.2.2 는 균질평형모델(HEM) 등 이상유동 " +
      "방법을 쓰도록 규정합니다. 산출한 이상유동 유량을 직접 입력하고, " +
      "밸브 사이징은 API 520 Annex C 방법(이 앱의 이상유동 사이징)으로 하십시오.",
  };
}
